import { useLocation } from 'react-router-dom';
/**
 * `CustomerProductPage` wraps the shared `ProductManager` for the customer
 * journey.
 *
 * Responsibilities:
 * - Builds a `ProductFilter` from current journey vehicle details.
 * - Configures the product flow for customer vs open-site channels.
 * - Controls whether quoted prices should be shown (CCW vs standard customers).
 * - Passes quote-expired state and acknowledgement handler into
 *   `ProductManager`.
 */
import { ProductChannel } from '../../../consts/Common';
import { ProductFilter } from '../../common/CommonTypes';
import { getFormattedDate } from '../../common/HelperMethods';
import ProductManager from '../../common/ProductPage/ProductManager';
import { useJourney } from '../Layout';
import { LocationQuote, LocationVehicleId } from './CustomerVehicleDetailsPage';
import { useAppSelector } from '../../../app-state/Hooks';

type LocationState = LocationQuote;

const CustomerProductPage = (): JSX.Element => {
  const userId = useAppSelector((s) => s.user.userId);
  const userToken = useAppSelector((s) => s.user.token);
  const appConfig = useAppSelector((s) => s.appConfig);
  const cms = useAppSelector((s) => s.content.productPage);

  const {
    journey,
    onUpdateProducts,
    onUpdateProductDocuments,
    onUpdateManufacturingCoverageMonths,
    setIsExpiredMessageAcknowledged
  } = useJourney();

  const {
    products,
    vehicle,
    quoteProducts,
    hasQuoteExpired,
    isExpiredMessageAcknowledged,
    isOpensiteUser,
    quoteId,
    expiryDetails
  } = journey;

  console.log(products);

  const { state } = useLocation();

  const isCCWUser = !!userId;

  const setIsMessageAcknowledged = () => {
    if (setIsExpiredMessageAcknowledged) {
      setIsExpiredMessageAcknowledged(true);
    }
  };

  const currentDate = new Date();
  const yesterdayDate = currentDate.setDate(currentDate.getDate() - 1);
  const gapDateOpensite = new Date(yesterdayDate).toLocaleDateString('en-GB');
  const defaultVehicleDeliveryDate = new Date(currentDate).toLocaleDateString(
    'en-GB'
  );

  const productFilter: ProductFilter = {
    dealerCode: journey.dealerCode,
    firstRegistrationDate: vehicle.vehicleDetails.registrationDate,
    vehiclePurchaseDate:
      vehicle.vehicleDeliveryDate === undefined
        ? defaultVehicleDeliveryDate
        : getFormattedDate(vehicle.vehicleDeliveryDate),
    vehicleValue: vehicle.vehiclePurchasePrice,
    currentMileage: vehicle.currentMileage ?? 0,
    modelTrimId: vehicle.vehicleDetails.modelTrimId,
    makeId: vehicle.vehicleDetails.makeId,
    modelGroupId: vehicle.vehicleDetails.modelId,
    fuelTypeId: vehicle.vehicleDetails.fuelTypeCode,
    engineSize: parseInt(vehicle.vehicleDetails.engineSize),
    enginePowerBHP: vehicle.vehicleDetails.enginePowerBHP ?? 0,
    enginePowerKWH: vehicle.vehicleDetails.enginePowerKWH ?? 0,
    driveTypeId: vehicle.vehicleDetails.driveTypeId?.toString() ?? '',
    vrn: vehicle.vehicleDetails.vrn,
    channel: isOpensiteUser ? ProductChannel.OpenSite : ProductChannel.Customer,
    gapDate:
      isOpensiteUser == true
        ? gapDateOpensite
        : getFormattedDate(vehicle.vehicleGapIntroductionDate) ?? '',
    isOpenSite: isOpensiteUser,
    countryCode: appConfig.countryCode
  };

  const showQuotedPrice = () => {
    if (isCCWUser) {
      return (
        (
          state as LocationState
        )?.quote?.vehicleInfo?.vehicleDetails?.vrn.toUpperCase() ===
        vehicle.vehicleDetails.vrn.toUpperCase()
      );
    } else {
      return true;
    }
  };

  return (
    <ProductManager
      products={products}
      productFilter={productFilter}
      foundExistingQuotes={quoteId > 0}
      cms={cms}
      onUpdateProducts={onUpdateProducts}
      onUpdateProductDocuments={onUpdateProductDocuments}
      onUpdateManufacturingCoverageMonths={onUpdateManufacturingCoverageMonths}
      token={userToken}
      quoteProducts={quoteProducts}
      hasQuoteExpired={hasQuoteExpired}
      isExpiredMessageAcknowledged={isExpiredMessageAcknowledged}
      setIsExpiredMessageAcknowledged={setIsMessageAcknowledged}
      showKeyBenefits={!isCCWUser}
      showKeyBenefitsInProductCard={true}
      showQuotedPrice={showQuotedPrice()}
      expiryDetails={expiryDetails}
      vehicleIdType={(state as LocationVehicleId)?.vehicleIdType}
    />
  );
};

export default CustomerProductPage;
